import React from "react";
import styles from "./attendance-analytics-card.module.css";
import { Card } from "../../../../shared/card/card.tsx";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAttendanceAnalytics } from "../../../../../hooks/use-attendance-analytics.ts";
import { ChartNoAxesCombined } from "lucide-react";
import { AttendanceEmptyState } from "../attendance-empty-state.tsx";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded p-2 shadow-sm">
      <div className="sub-heading-sm">{label}</div>
      <div className="heading-xs">{payload[0].value}%</div>
    </div>
  );
};

const AttendanceAnalyticsCard = () => {
  const { data, loading } = useAttendanceAnalytics();

  if (loading) return <Card>Loading...</Card>;

  const showEmptyState =
    !Array.isArray(data?.monthlyData) || data.monthlyData.length === 0;

  const chartData =
    data?.monthlyData?.map((item) => ({
      month:
        item.month.slice(0, 1) +
        item.month.slice(1, 3).toLowerCase(),
      percentage: item.percentage,
    })) || [];

  const currentMonth =
    data?.monthlyData?.[data.monthlyData.length - 1]?.percentage || 0;

  return (
    <Card className={showEmptyState ? styles.emptyCard : undefined}>
      <div className={showEmptyState ? styles.emptyContainer : styles.container}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <h5 className="heading-sm">Attendance Analytics</h5>
            <div className="sub-heading-sm">6-month performance trend</div>
          </div>

          {/* <div className={styles.trend}>+6% vs last quarter</div> */}
        </div>

        {showEmptyState ? (
          <AttendanceEmptyState
            icon={ChartNoAxesCombined}
            title="Analytics Unavailable"
            description="Attendance insights couldn't be displayed at the moment."
          />
        ) : (
          <>
            <div className={styles.chartWrap}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="percentage"
                    stroke="var(--color-accent-500)"
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className={styles.divider}></div>

            <div className={styles.stats}>
              <div className={styles.statItem}>
                <span className="sub-heading-sm">Current Month</span>
                <span className="heading-xl font-700">{currentMonth}%</span>
              </div>

              <div className={styles.statItem}>
                <span className="sub-heading-sm">This Week</span>
                <span className={` heading-xl font-700  ${styles.statPrimary}`}>
                  {data?.weeklyWorkingDays}/ 5
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default AttendanceAnalyticsCard;
